/**
 * The one row the review screen renders per pick slip (specs/challenge-flow-redesign Story 4,
 * specs/formula-one-challenges).
 *
 * Extracted from CreateReviewStep so it can be tested directly: this is the last screen before
 * coins are committed, and it was describing a Grand Prix as a fixture — `createApi` maps a
 * race's round into `homeTeam` and its venue into `awayTeam`, so the generic "home v away" line
 * read "Netherlands Grand Prix v Circuit Zandvoort".
 */

import { getSportDefinitionOrDefault, OUTCOME_SHAPES } from '../../sports/sports.constants';
import { isMatchWinnerSlip } from '../types/create.types';
import { readablePickValue } from './pickLabel.helper';
import type { Game, PickSlip } from '../types/create.types';

const PRED_LABEL: Record<'home' | 'draw' | 'away', string> = { home: 'Home', draw: 'Draw', away: 'Away' };

export function slipRow(
  slip: PickSlip,
  game: Game | undefined
): { home: string; away: string; match: string; side: string; odds: number } {
  const home = game?.homeTeam ?? slip.homeTeam ?? 'Home';
  const away = game?.awayTeam ?? slip.awayTeam ?? 'Away';

  // A FIELD fixture has no pairing to describe. `createApi` maps a race's round into `homeTeam`
  // and its venue into `awayTeam`, so the generic "home v away" line rendered as
  // "Netherlands Grand Prix v Circuit Zandvoort" — on the last screen before coins are committed.
  if (getSportDefinitionOrDefault(game?.sport).shape === OUTCOME_SHAPES.FIELD) {
    const eventName = game?.eventName ?? home;
    const circuit = game?.venue;
    return {
      home,
      away,
      match: circuit != null && circuit !== '' ? `${eventName} · ${circuit}` : eventName,
      side: `${slip.marketName ?? 'Pick'} · ${readablePickValue(slip.marketId, slip.value)}`,
      odds: slip.odd ?? 0,
    };
  }

  if (isMatchWinnerSlip(slip)) {
    const side = slip.prediction;
    const team = side === 'home' ? home : side === 'away' ? away : 'Draw';
    const odds = game
      ? ((side === 'home' ? game.odds.home : side === 'away' ? game.odds.away : game.odds.draw) ?? 0)
      : 0;
    return { home, away, match: `${home} v ${away}`, side: `${PRED_LABEL[side]} · ${team}`, odds };
  }
  return { home, away, match: `${home} v ${away}`, side: `${slip.marketName ?? 'Pick'} · ${readablePickValue(slip.marketId, slip.value)}`, odds: slip.odd ?? 0 };
}
